// Servicio para manejar operaciones de concursos con la base de datos
import { environment } from '../config/environment';
import { getApiHeaders, getApiHeadersForGet } from '../utils/apiUtils';

class ContestService {
  constructor() {
    this.baseUrl = environment.apiBaseUrl; // URL del servidor backend (configurado según entorno)
  }

  // Fetch con reintentos ante 429 (Too Many Requests) en producción
  async _fetchWithRetry(url, options = {}, maxRetries = 3) {
    let lastResponse;
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      lastResponse = await fetch(url, options);
      if (lastResponse.status === 429 && attempt < maxRetries) {
        const delayMs = attempt * 1500;
        await new Promise(r => setTimeout(r, delayMs));
        continue;
      }
      return lastResponse;
    }
    return lastResponse;
  }

  // Crear un nuevo concurso
  async createContest(contestData) {
    try {
      const response = await fetch(`${this.baseUrl}/contests`, {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify(contestData)
      });

      if (!response.ok) {
        let body = {};
        try {
          body = await response.json();
        } catch (_) {}
        const err = new Error(body.message || `Error ${response.status}: ${response.statusText}`);
        err.isDuplicateName = body.error === 'A contest with this name already exists';
        err.status = response.status;
        err.body = body;
        throw err;
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error creating contest:', error);
      throw error;
    }
  }

  // Obtener todos los concursos (con reintentos ante 429 en producción)
  async getAllContests(forceRefresh = false) {
    try {
      const url = forceRefresh
        ? `${this.baseUrl}/contests?_t=${Date.now()}`
        : `${this.baseUrl}/contests`;

      const response = await this._fetchWithRetry(url, {
        headers: getApiHeadersForGet()
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const contests = await response.json();

      // Generar datos faltantes para cada concurso
      return contests.map(contest => this.generateMissingData(contest));
    } catch (error) {
      console.error('Error fetching contests:', error);
      throw error;
    }
  }

  // Generar datos faltantes para el concurso
  generateMissingData(contest) {
    if (contest.name === 'Concurso de Prueba para Patrocinio') {
      console.log('Generating missing data for contest:', contest.name);
    }
    
    // Generar membership_breakdown
    const membershipBreakdown = this.generateMembershipBreakdown(contest);
    
    // Usar economics del servidor si existe (incluye vote boosters y tags)
    // Solo generar economics si no existe o no tiene total_revenue
    let economics = contest.economics;
    if (!economics || economics.total_revenue === undefined) {
      economics = this.generateEconomics(contest, membershipBreakdown);
    }
    
    // Solo generar sponsorship si no hay datos reales de patrocinio
    let sponsorship = contest.sponsorship; // Usar datos reales si existen
    if (!sponsorship) {
      sponsorship = this.generateSponsorship(contest);
    }
    
    return {
      ...contest,
      membership_breakdown: membershipBreakdown,
      economics: economics,
      sponsorship: sponsorship
    };
  }

  // Generar breakdown de membresías
  generateMembershipBreakdown(contest) {
    const totalParticipants = contest.current_participants || contest.min_participants || 100;
    const entranceFee = contest.entrance_fee || 5;
    
    // Si no hay participantes, generar datos de ejemplo
    if (contest.current_participants === 0) {
      return this.generateExampleMembershipBreakdown(entranceFee, totalParticipants);
    }

    const breakdown = [];
    
    // Voucher Free Pass (20% de participantes)
    const voucherCount = Math.floor(totalParticipants * 0.2);
    if (voucherCount > 0) {
      breakdown.push({
        membership_key: 'voucherFreePass',
        discount_type: 'Voucher Free Pass',
        membership_level: 'Any (voucher holder)',
        base_price: entranceFee,
        actual_paid: 0.00,
        discount_amount: entranceFee,
        discount_percentage: 100.00,
        participant_count: voucherCount,
        total_revenue: 0.00,
        percentage_of_total: (voucherCount / totalParticipants) * 100
      });
    }

    // Subscribers Discounted (40% de participantes)
    const subscriberCount = Math.floor(totalParticipants * 0.4);
    if (subscriberCount > 0) {
      const discountedPrice = entranceFee * 0.5;
      breakdown.push({
        membership_key: 'subscribersDiscounted',
        discount_type: 'Noah\'s Ark Subscribers (50% off)',
        membership_level: 'Paid Subscriber',
        base_price: entranceFee,
        actual_paid: discountedPrice,
        discount_amount: entranceFee - discountedPrice,
        discount_percentage: 50.00,
        participant_count: subscriberCount,
        total_revenue: subscriberCount * discountedPrice,
        percentage_of_total: (subscriberCount / totalParticipants) * 100
      });
    }

    // Members Full Price (30% de participantes)
    const memberCount = Math.floor(totalParticipants * 0.3);
    if (memberCount > 0) {
      breakdown.push({
        membership_key: 'membersFullPrice',
        discount_type: 'Members - Full Price',
        membership_level: 'Free Member',
        base_price: entranceFee,
        actual_paid: entranceFee,
        discount_amount: 0.00,
        discount_percentage: 0.00,
        participant_count: memberCount,
        total_revenue: memberCount * entranceFee,
        percentage_of_total: (memberCount / totalParticipants) * 100
      });
    }

    // Non-Members Full Price (resto)
    const nonMemberCount = totalParticipants - voucherCount - subscriberCount - memberCount;
    if (nonMemberCount > 0) {
      breakdown.push({
        membership_key: 'nonMembersFull',
        discount_type: 'Non-Members - Full Price',
        membership_level: 'Public',
        base_price: entranceFee,
        actual_paid: entranceFee,
        discount_amount: 0.00,
        discount_percentage: 0.00,
        participant_count: nonMemberCount,
        total_revenue: nonMemberCount * entranceFee,
        percentage_of_total: (nonMemberCount / totalParticipants) * 100
      });
    }

    return breakdown;
  }

  // Generar datos de ejemplo para membresías cuando no hay participantes
  generateExampleMembershipBreakdown(entranceFee, totalParticipants) {
    const breakdown = [];
    
    // Voucher Free Pass (20% de participantes)
    const voucherCount = Math.floor(totalParticipants * 0.2);
    if (voucherCount > 0) {
      breakdown.push({
        membership_key: 'voucherFreePass',
        discount_type: 'Voucher Free Pass',
        membership_level: 'Any (voucher holder)',
        base_price: entranceFee,
        actual_paid: 0.00,
        discount_amount: entranceFee,
        discount_percentage: 100.00,
        participant_count: voucherCount,
        total_revenue: 0.00,
        percentage_of_total: (voucherCount / totalParticipants) * 100
      });
    }

    // Subscribers Discounted (40% de participantes)
    const subscriberCount = Math.floor(totalParticipants * 0.4);
    if (subscriberCount > 0) {
      const discountedPrice = entranceFee * 0.5;
      breakdown.push({
        membership_key: 'subscribersDiscounted',
        discount_type: 'Noah\'s Ark Subscribers (50% off)',
        membership_level: 'Paid Subscriber',
        base_price: entranceFee,
        actual_paid: discountedPrice,
        discount_amount: entranceFee - discountedPrice,
        discount_percentage: 50.00,
        participant_count: subscriberCount,
        total_revenue: subscriberCount * discountedPrice,
        percentage_of_total: (subscriberCount / totalParticipants) * 100
      });
    }

    // Members Full Price (30% de participantes)
    const memberCount = Math.floor(totalParticipants * 0.3);
    if (memberCount > 0) {
      breakdown.push({
        membership_key: 'membersFullPrice',
        discount_type: 'Members - Full Price',
        membership_level: 'Free Member',
        base_price: entranceFee,
        actual_paid: entranceFee,
        discount_amount: 0.00,
        discount_percentage: 0.00,
        participant_count: memberCount,
        total_revenue: memberCount * entranceFee,
        percentage_of_total: (memberCount / totalParticipants) * 100
      });
    }

    // Non-Members Full Price (resto)
    const nonMemberCount = totalParticipants - voucherCount - subscriberCount - memberCount;
    if (nonMemberCount > 0) {
      breakdown.push({
        membership_key: 'nonMembersFull',
        discount_type: 'Non-Members - Full Price',
        membership_level: 'Public',
        base_price: entranceFee,
        actual_paid: entranceFee,
        discount_amount: 0.00,
        discount_percentage: 0.00,
        participant_count: nonMemberCount,
        total_revenue: nonMemberCount * entranceFee,
        percentage_of_total: (nonMemberCount / totalParticipants) * 100
      });
    }

    return breakdown;
  }

  // Generar datos económicos
  generateEconomics(contest, membershipBreakdown) {
    const totalParticipants = contest.current_participants || 0;
    const entranceFee = contest.entrance_fee || 0;
    const mainPrize = Number(contest.main_prize) || 0;
    const secondaryPrize = Number(contest.secondary_prize) || 0;
    const prizePoolTotal = Number(contest.prize_pool_total) || (mainPrize + secondaryPrize);
    
    // Si no hay participantes reales, no usar el breakdown de ejemplo para calcular ingresos
    // Los ingresos deben ser 0 cuando no hay participantes, patrocinadores, vote boosters o tags
    const participantRevenue = totalParticipants === 0 ? 0 : membershipBreakdown.reduce((sum, item) => sum + item.total_revenue, 0);
    // El patrocinador FINANCIA el premio: su aporte (reward_contribution) cubre el
    // costo del premio. Se usa el mayor entre el costo de paquete y el aporte de premio.
    const sponsorCost = contest.sponsorship
      ? Math.max(Number(contest.sponsorship.total_sponsor_cost) || 0, Number(contest.sponsorship.reward_contribution) || 0)
      : 0;
    const totalRevenue = participantRevenue + sponsorCost;
    // Costos = pozo total de premios (consistente con el backend), sin un ops cost extra.
    const totalCosts = prizePoolTotal;
    const profit = totalRevenue - totalCosts;
    const profitMargin = totalRevenue > 0 ? (profit / totalRevenue) * 100 : 0;
    const roi = totalCosts > 0 ? (profit / totalCosts) * 100 : 0;
    
    const totalDiscounts = membershipBreakdown.reduce((sum, item) => sum + (item.discount_amount * item.participant_count), 0);
    const potentialRevenue = totalParticipants * entranceFee;
    const revenueEfficiency = potentialRevenue > 0 ? (participantRevenue / potentialRevenue) * 100 : 0;
    
    return {
      sponsor_cost: sponsorCost,
      participant_revenue: participantRevenue,
      organizer_revenue: 0,
      total_revenue: totalRevenue,
      total_costs: totalCosts,
      prize_costs: mainPrize,
      ops_costs: secondaryPrize,
      noahs_ark_profit: profit,
      profit_margin: profitMargin,
      total_roi: roi,
      revenue_per_participant: totalParticipants > 0 ? participantRevenue / totalParticipants : 0,
      cost_per_participant: totalParticipants > 0 ? totalCosts / totalParticipants : 0,
      voucher_redemption_value: membershipBreakdown.find(item => item.membership_key === 'voucherFreePass')?.total_revenue || 0,
      total_discount_given: totalDiscounts,
      full_price_revenue: membershipBreakdown.filter(item => item.discount_percentage === 0).reduce((sum, item) => sum + item.total_revenue, 0),
      discounted_revenue: membershipBreakdown.filter(item => item.discount_percentage > 0).reduce((sum, item) => sum + item.total_revenue, 0),
      weighted_average_price: totalParticipants > 0 ? participantRevenue / totalParticipants : 0,
      breakeven_participants: entranceFee > 0 ? Math.ceil(totalCosts / entranceFee) : 0,
      revenue_efficiency_percentage: revenueEfficiency,
      potential_revenue: potentialRevenue
    };
  }

  // El patrocinio real ya viene adjunto desde el servidor (join con `sponsorships`
  // cuando el concurso tiene sponsor_id). No se debe inventar un patrocinador
  // falso: un concurso sin sponsor_id enlazado nunca debe mostrar sponsor.
  generateSponsorship(_contest) {
    return null;
  }

  // Obtener un concurso por ID
  async getContestById(id) {
    try {
      const response = await fetch(`${this.baseUrl}/contests/${id}`, {
        headers: getApiHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const contest = await response.json();
      return contest;
    } catch (error) {
      console.error('Error fetching contest:', error);
      throw error;
    }
  }

  // Actualizar un concurso
  async updateContest(id, contestData) {
    try {
      const response = await fetch(`${this.baseUrl}/contests/${id}`, {
        method: 'PUT',
        headers: getApiHeaders(),
        body: JSON.stringify(contestData)
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error updating contest:', error);
      throw error;
    }
  }

  // Actualizar el sponsor de un concurso
  async updateContestSponsor(contestId, sponsorId) {
    try {
      const response = await fetch(`${this.baseUrl}/contests/${contestId}/sponsor`, {
        method: 'PATCH',
        headers: getApiHeaders(),
        body: JSON.stringify({ sponsorId })
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error updating contest sponsor:', error);
      throw error;
    }
  }

  // Eliminar un concurso
  async deleteContest(id) {
    try {
      const response = await fetch(`${this.baseUrl}/contests/${id}`, {
        method: 'DELETE',
        headers: getApiHeaders()
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error deleting contest:', error);
      throw error;
    }
  }

  // Obtener datos económicos reales de un concurso
  async getContestEconomics(contestId) {
    try {
      const response = await fetch(`${this.baseUrl}/contests/${contestId}/economics`, {
        headers: getApiHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error fetching contest economics:', error);
      throw error;
    }
  }
}

export default new ContestService();
